<template>
    <a-modal
        :visible="true"
        width="80vw"
        :footer="null"
        :closable="false"
        :destroy-on-close="true"
        centered
        wrap-class-name="ppm-wrap"
        @cancel="$emit('close')"
    >
        <div class="ppm-modal">
            <div class="ppm-head">
                <div>
                    <h3>Produtos do fornecedor</h3>
                    <div class="ppm-sub">{{ provider.name }}</div>
                </div>
                <a-tooltip title="Fechar">
                    <a-button class="ppm-x" @click="$emit('close')">
                        <span class="material-icons-outlined">close</span>
                    </a-button>
                </a-tooltip>
            </div>

            <div class="ppm-tools">
                <a-input
                    v-model="search"
                    class="ppm-search"
                    placeholder="Buscar por EAN ou descrição"
                    allow-clear
                    @change="onSearch"
                    @pressEnter="loadPage(1)"
                >
                    <a-icon slot="prefix" type="search" />
                </a-input>
                <a-button class="ppm-tools-right" :loading="exporting" @click="exportExcel">
                    <i class="fa-solid fa-file-excel ppm-btn-icon"></i> Exportar Excel
                </a-button>
            </div>

            <div class="ppm-body">
                <a-table
                    size="small"
                    :columns="columns"
                    :data-source="products"
                    :row-key="(record, index) => index"
                    :loading="loading"
                    :pagination="pagination"
                    :scroll="{ x: 'max-content' }"
                    :locale="{ emptyText: 'Nenhum produto encontrado para este fornecedor.' }"
                    @change="(p) => loadPage(p.current)"
                >
                    <template slot="text" slot-scope="text">{{ text || "--" }}</template>
                    <template slot="money" slot-scope="text">
                        <span class="ppm-money">{{ text != null ? $options.filters.currency(text) : "--" }}</span>
                    </template>
                    <template slot="date" slot-scope="text">{{ formatDate(text) }}</template>
                </a-table>
            </div>

            <div class="ppm-foot">
                <a-button @click="$emit('close')">Fechar</a-button>
            </div>
        </div>
    </a-modal>
</template>

<script>
import moment from "moment";
import ProductService from "@/services/v3/products/product.service";
import ErrorHandlerService from "@/services/ErrorHandlerService";

export default {
    name: "ProviderProductsV2Modal",
    props: {
        provider: { type: Object, required: true },
    },
    data() {
        return {
            svc: new ProductService(),
            loading: false,
            exporting: false,
            products: [],
            search: "",
            searchTimeout: null,
            pagination: {
                current: 1,
                pageSize: 50,
                total: 0,
                showSizeChanger: false,
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} itens`,
            },
            columns: [
                { title: "Código", dataIndex: "pro_ean", key: "pro_ean" },
                { title: "Descrição", dataIndex: "pro_descricao", key: "pro_descricao" },
                { title: "Marca", dataIndex: "marca", key: "marca", scopedSlots: { customRender: "text" } },
                { title: "Embalagem", dataIndex: "pro_embalagem", key: "pro_embalagem" },
                { title: "Preço Última Compra", dataIndex: "pro_ultimaCompra", key: "pro_ultimaCompra", align: "right", scopedSlots: { customRender: "money" } },
                { title: "Data Última Compra", dataIndex: "data_ultimaCompra", key: "data_ultimaCompra", align: "center", scopedSlots: { customRender: "date" } },
                { title: "Última Digitação", dataIndex: "last_offer", key: "last_offer", align: "right", scopedSlots: { customRender: "money" } },
                { title: "Data Última Digitação", dataIndex: "updated_at", key: "updated_at", align: "center", scopedSlots: { customRender: "date" } },
            ],
        };
    },
    created() {
        this.loadPage(1);
    },
    beforeDestroy() {
        clearTimeout(this.searchTimeout);
    },
    methods: {
        searchTerm() {
            return (this.search || "").trim() || null;
        },
        formatDate(date) {
            return date ? moment(String(date)).format("DD/MM/YYYY") : "--";
        },
        onSearch() {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => this.loadPage(1), 400);
        },
        loadPage(page) {
            clearTimeout(this.searchTimeout);
            this.loading = true;
            this.svc
                .get_products(this.provider.id, page, this.searchTerm())
                .then(({ data }) => {
                    this.products = data.data || [];
                    this.pagination.total = data.total || 0;
                    this.pagination.current = page;
                })
                .catch((error) => {
                    this.products = [];
                    this.pagination.total = 0;
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        exportExcel() {
            this.exporting = true;
            this.svc
                .export_products_report(this.provider.id, this.searchTerm())
                .then(({ data }) => {
                    const url = URL.createObjectURL(new Blob([data], { type: "application/vnd.ms-excel" }));
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = `Produtos-${this.provider.name}.xls`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    setTimeout(() => URL.revokeObjectURL(url), 100);
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.exporting = false;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.ppm-modal {
    max-height: 88vh;
    background: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.ppm-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 18px 20px 14px;

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #262626;
    }
}
.ppm-sub {
    font-size: 13px;
    color: #8c8c8c;
    margin-top: 2px;
}
.ppm-x.ant-btn,
.ppm-x.ant-btn:focus,
.ppm-x.ant-btn:active {
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    background: none;
    border-radius: 6px;
    color: #8c8c8c;
    box-shadow: none;
    display: flex;
    align-items: center;
    justify-content: center;
}
.ppm-x.ant-btn:hover {
    background: #f0f0f0;
    color: #262626;
}
.ppm-tools {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    padding: 0 20px 12px;
}
.ppm-search {
    flex: 1 1 240px;
    max-width: 360px;
}
.ppm-tools-right {
    margin-left: auto;
}
.ppm-btn-icon {
    margin-right: 6px;
}
.ppm-body {
    flex: 1;
    overflow-y: auto;
    padding: 4px 20px 12px;
    border-top: 1px solid #f0f0f0;
    min-height: 200px;
}
.ppm-money {
    white-space: nowrap;
}
.ppm-foot {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 14px 20px;
    border-top: 1px solid #f0f0f0;
}
</style>

<style lang="scss">
.ppm-wrap {
    .ant-modal {
        min-width: 560px;
        max-width: 95vw;
    }

    .ant-modal-content {
        padding: 0;
        border-radius: 10px;
        overflow: hidden;
    }

    .ant-modal-body {
        padding: 0;
    }
}
</style>
